import { ChevronLeft, ChevronRight, X, ZoomIn, ZoomOut } from 'lucide-react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

interface ImageLightboxProps {
  images: string[]
  index: number
  onClose: () => void
  onIndexChange: (index: number) => void
}

const MIN_ZOOM = 1
const MAX_ZOOM = 3

export function ImageLightbox({
  images,
  index,
  onClose,
  onIndexChange
}: ImageLightboxProps) {
  const [zoom, setZoom] = useState(1)
  const count = images.length

  const go = useCallback(
    (delta: number) => {
      setZoom(1)
      onIndexChange((index + delta + count) % count)
    },
    [index, count, onIndexChange]
  )

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
      else if (e.key === 'ArrowRight') go(1)
      else if (e.key === 'ArrowLeft') go(-1)
    }
    window.addEventListener('keydown', onKey)
    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      window.removeEventListener('keydown', onKey)
      document.body.style.overflow = prevOverflow
    }
  }, [go, onClose])

  const stop = (e: React.MouseEvent) => e.stopPropagation()
  const zoomIn = () => setZoom((z) => Math.min(z + 0.5, MAX_ZOOM))
  const zoomOut = () => setZoom((z) => Math.max(z - 0.5, MIN_ZOOM))

  // Touch swipe to navigate (only when not zoomed).
  const touchStart = useRef<{ x: number; y: number } | null>(null)
  const onTouchStart = (e: React.TouchEvent) => {
    const touch = e.touches[0]
    if (touch) touchStart.current = { x: touch.clientX, y: touch.clientY }
  }
  const onTouchEnd = (e: React.TouchEvent) => {
    const start = touchStart.current
    touchStart.current = null
    if (!start || zoom > 1 || count < 2) return
    const touch = e.changedTouches[0]
    if (!touch) return
    const dx = touch.clientX - start.x
    const dy = touch.clientY - start.y
    if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy)) go(dx < 0 ? 1 : -1)
  }

  const btn =
    'h-10 w-10 rounded-full bg-white/10 text-white hover:bg-white/20 hover:text-white disabled:opacity-40'

  return createPortal(
    <div
      className="fixed inset-0 z-[1000] flex touch-none items-center justify-center bg-ink/95 backdrop-blur-sm"
      onClick={onClose}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      {/* Top bar */}
      <div className="absolute inset-x-0 top-0 flex items-center justify-between p-4">
        <span className="rounded-full bg-white/10 px-3 py-1 text-sm font-medium text-white">
          {index + 1} / {count}
        </span>
        <div className="flex items-center gap-2" onClick={stop}>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            aria-label="zoom out"
            className={btn}
            onClick={zoomOut}
            disabled={zoom <= MIN_ZOOM}
          >
            <ZoomOut className="h-5 w-5" />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            aria-label="zoom in"
            className={btn}
            onClick={zoomIn}
            disabled={zoom >= MAX_ZOOM}
          >
            <ZoomIn className="h-5 w-5" />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            aria-label="close"
            className={btn}
            onClick={onClose}
          >
            <X className="h-5 w-5" />
          </Button>
        </div>
      </div>

      {count > 1 && (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          aria-label="previous"
          className={cn(btn, 'absolute left-4 top-1/2 -translate-y-1/2')}
          onClick={(e) => {
            stop(e)
            go(-1)
          }}
        >
          <ChevronLeft className="h-6 w-6" />
        </Button>
      )}

      <img
        src={images[index]}
        alt=""
        onClick={(e) => {
          stop(e)
          setZoom((z) => (z > 1 ? 1 : 2))
        }}
        style={{ transform: `scale(${zoom})` }}
        className={cn(
          'max-h-[85vh] max-w-[90vw] select-none object-contain transition-transform duration-200',
          zoom > 1 ? 'cursor-zoom-out' : 'cursor-zoom-in'
        )}
      />

      {count > 1 && (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          aria-label="next"
          className={cn(btn, 'absolute right-4 top-1/2 -translate-y-1/2')}
          onClick={(e) => {
            stop(e)
            go(1)
          }}
        >
          <ChevronRight className="h-6 w-6" />
        </Button>
      )}
    </div>,
    document.body
  )
}
